/**
 * Shop module — магазин расходников «Упаковка». Каталог хардкодится в коде:
 * мы пока не добавляем новых товаров (см. PACKING_REDESIGN_PLAN.md).
 *
 * Каталог + статусы + zod-схемы лежат в shared, чтобы клиент и сервер
 * считали одинаково: цена/имя/иллюстрация снапшотятся при создании заказа.
 */
import { z } from "zod";
import { courierIntakeSchema } from "../delivery/schemas.js";
export const SHOP_CATALOG = [
    {
        key: "boxes",
        title: "Коробки",
        shortMeta: "от 3 ₾ · 4 размера",
        items: [
            { id: "box-s", name: "Коробка малая", dims: "34×27×14 см", layers: 3, maxKg: 10, price: 3, illu: "box-small", inStock: true },
            { id: "box-m", name: "Коробка средняя", dims: "36×27×18 см", layers: 3, maxKg: 15, price: 4, illu: "box-medium", inStock: true },
            { id: "box-l", name: "Коробка большая", dims: "40×34×34 см", layers: 3, maxKg: 20, price: 5, illu: "box-large", inStock: false },
            { id: "box-xl", name: "Коробка XL пятислойная", dims: "60×40×40 см", layers: 5, maxKg: 30, price: 8, illu: "box-xl", premium: true, inStock: true },
        ],
    },
    {
        key: "vacuum",
        title: "Вакуумные пакеты",
        shortMeta: "5 ₾ · 4 размера",
        promo: "Насос в подарок от 10 шт",
        items: [
            { id: "vac-40x60", name: "Вакуумный пакет", dims: "40×60 см", price: 5, illu: "vacuum", inStock: true },
            { id: "vac-50x70", name: "Вакуумный пакет", dims: "50×70 см", price: 5, illu: "vacuum", inStock: true },
            { id: "vac-60x80", name: "Вакуумный пакет", dims: "60×80 см", price: 5, illu: "vacuum", inStock: true },
            { id: "vac-80x100", name: "Вакуумный пакет", dims: "80×100 см", price: 5, illu: "vacuum", inStock: true },
        ],
    },
    {
        key: "bubble",
        title: "Воздушно-пузырчатая плёнка",
        shortMeta: "8 ₾ / погонный метр",
        items: [
            { id: "bubble-1", name: "Пузырчатая плёнка", dims: "Ширина рулона 1,5 м", price: 8, perUnit: "погонный метр", illu: "bubble", inStock: true },
        ],
    },
    {
        key: "tape",
        title: "Скотч",
        shortMeta: "5 ₾ / штука",
        items: [
            { id: "tape-1", name: "Скотч упаковочный", dims: "48 мм × 50 м", price: 5, illu: "tape", inStock: true },
        ],
    },
];
/** Кол-во вакуумных пакетов, при котором добавляется бесплатный насос. */
export const VACUUM_GIFT_THRESHOLD = 10;
export function getShopCategory(key) {
    return SHOP_CATALOG.find((c) => c.key === key);
}
export function getShopProduct(id) {
    for (const cat of SHOP_CATALOG) {
        const p = cat.items.find((i) => i.id === id);
        if (p)
            return p;
    }
    return undefined;
}
// ============================================================
// Статусы заказа магазина
// pending — ждём подтверждения наличия менеджером (в течение 30 мин)
// awaiting_payment — ждём дистанционную оплату/доплату
// paid — оплата подтверждена (чек / ручная проверка / касса на месте)
// ready   — подтверждено, можно забирать на складе по QR
// completed — выдано
// cancelled — отменено ДО оплаты (нет в наличии / клиент передумал отправлять —
//             решение владельца 2026-06-13, §11 п.2 мастер-плана)
// refused — клиент отказался при выдаче (пишет сканер личного QR, executeBatch)
// ============================================================
export const SHOP_ORDER_STATUSES = [
    "pending",
    // Review-цикл (2026-05-29): первый GET карточки админом авто-переключает
    // pending → in_review. Из in_review админ жмёт «Принять» (remote →
    // awaiting_payment, on_pickup → ready) или «Отклонить» (→ rejected,
    // юзер видит причину и может пересоздать).
    "in_review",
    "rejected",
    "awaiting_payment",
    "paid",
    "ready",
    "completed",
    "cancelled",
    // Карточка shop-status-flow-shared (2026-06-13): refused добавлен в канон —
    // сканер выдачи реально писал его в БД (delivery.service REFUSED_TO.shop),
    // статуса в словаре не было. Терминальный; миграции не нужны (status=String).
    "refused",
];
export const SHOP_ORDER_STATUS_LABELS_RU_CLIENT = {
    pending: "Ждём подтверждения",
    in_review: "На рассмотрении",
    rejected: "Отклонён",
    awaiting_payment: "Ждём оплату",
    paid: "Оплачен",
    ready: "Готово к выдаче",
    completed: "Выдано",
    cancelled: "Отменён",
    refused: "Отказ при получении",
};
export const SHOP_ORDER_STATUS_LABELS_RU_ADMIN = {
    pending: "Новый",
    in_review: "На рассмотрении",
    rejected: "Отклонён",
    awaiting_payment: "Ждёт оплату",
    paid: "Оплачен",
    ready: "Подтверждён",
    completed: "Выдано",
    cancelled: "Отменён",
    refused: "Отказ при выдаче",
};
// ============================================================
// Канонический граф статусов shop-заказа.
// Карточка shop-status-flow-shared (мастер-план §6.1) + решения владельца
// 2026-06-13 (§11 п.2): «paid» от кассы легитимен (деньги поступили — кэш в
// обороте кассы); «cancelled» = передумали до оплаты.
//
// Единственная функция перехода на сервере —
// apps/api/src/services/shopStatus.service.ts:transitionShopOrderStatus
// (CAS по from-статусу + ВСЕГДА строка в orderStatusLog). Прямые записи
// shopOrder.status мимо неё закрыты gate-тестом shopStatus.contract.test.ts.
// ============================================================
/** Способ оплаты shop-заказа: на месте при выдаче или дистанционно по реквизитам. */
export const SHOP_PAYMENT_METHODS = ["on_pickup", "remote"];
/**
 * Эффективный способ оплаты заказа. Правило одно на всех потребителей:
 * courier и self_courier — ВСЕГДА дистанционная оплата (форсится и при
 * создании заказа); для pickup действует paymentMethod, по умолчанию on_pickup.
 */
export function effectiveShopPaymentMethod(order) {
    if (order.method === "courier" || order.method === "self_courier")
        return "remote";
    return order.paymentMethod === "remote" ? "remote" : "on_pickup";
}
/** Терминальные статусы — выходов нет. */
export const SHOP_ORDER_TERMINAL_STATUSES = Object.freeze([
    "completed",
    "cancelled",
    "refused",
]);
/**
 * Отмена (админская, cancelShopOrder) разрешена только ДО оплаты — из paid
 * деньги уже получены, процедуры возврата нет (карточка §4.12 + решение
 * владельца 2026-06-13). Из paid/ready отмена остаётся ОТДЕЛЬНОЙ админской
 * операцией с ручным разбором денег — в графе её нет.
 */
export const SHOP_CANCELLABLE_STATUSES = Object.freeze([
    "pending",
    "in_review",
    "awaiting_payment",
]);
/**
 * Статусы, из которых принимается чек дистанционной оплаты (submitShopReceipt).
 * in_review добавлен карточкой shop-status-flow-shared: клиент, чью карточку
 * открыл админ (pending → in_review авто-латч), больше не ловит 409 за честную
 * загрузку чека.
 */
export const SHOP_RECEIPT_ACCEPTING_STATUSES = Object.freeze([
    "pending",
    "in_review",
    "awaiting_payment",
]);
/**
 * Статусы, из которых заказ реально выдаётся/отказывается сканером личного QR
 * (delivery.service: DELIVERABLE_FROM.shop и источник refuse). Решение
 * владельца 2026-06-13: «не оплаченный заказ выдать НЕЛЬЗЯ» — awaiting_payment
 * исключён; on_pickup-оплата фиксируется кассой в самом executeBatch.
 */
export const SHOP_DELIVERABLE_FROM_STATUSES = Object.freeze([
    "paid",
    "ready",
]);
/** Целевой статус выдачи (deliver) сканера личного QR. */
export const SHOP_DELIVERED_STATUS = "completed";
/** Целевой статус отказа (refuse) сканера личного QR. */
export const SHOP_REFUSED_STATUS = "refused";
/**
 * SHOP_ORDER_STATUS_FLOW — разрешённые переходы по способу оплаты.
 *
 * on_pickup (канон, решение оркестратора 2026-06-13):
 *   pending → in_review → {ready | rejected}; ready → completed через
 *   executeBatch (оплата на месте фиксируется кассой в той же транзакции);
 *   paid допустим как промежуточный — касса может зафиксировать оплату ДО
 *   выдачи (ready→paid / awaiting_payment→paid), после чего скан выдаёт
 *   paid→completed. awaiting_payment у on_pickup — легаси-дренаж: туда вели
 *   старый approveShopOrder и линейный getShopStatusTransition до этой карточки.
 *
 * remote (канон):
 *   pending → in_review → {awaiting_payment | rejected};
 *   awaiting_payment → paid; paid → ready → completed.
 *   Чек принимается и из pending (легаси-поведение: клиент платит до открытия
 *   карточки админом), и из in_review (фикс 409): полный чек → paid,
 *   частичный → awaiting_payment.
 *
 * Общие ветки:
 *   pending|in_review → rejected (admin reject; из pending — узкое окно до
 *   авто-латча first-view), rejected → pending (PATCH /shop/orders/:id/resubmit);
 *   {pending, in_review, awaiting_payment} → cancelled (admin cancel до оплаты);
 *   {paid, ready} → completed|refused (executeBatch личного QR).
 */
export const SHOP_ORDER_STATUS_FLOW = {
    on_pickup: {
        pending: ["in_review", "rejected", "cancelled"],
        in_review: ["ready", "rejected", "cancelled"],
        rejected: ["pending"],
        // Легаси-дренаж + касса: paid — оплата на месте до выдачи; ready — заказ
        // подтверждён, расчёт на стойке при выдаче; cancelled — отмена до оплаты.
        awaiting_payment: ["paid", "ready", "cancelled"],
        paid: ["ready", "completed", "refused"],
        ready: ["paid", "completed", "refused"],
        completed: [],
        cancelled: [],
        refused: [],
    },
    remote: {
        pending: ["in_review", "awaiting_payment", "paid", "rejected", "cancelled"],
        in_review: ["awaiting_payment", "paid", "rejected", "cancelled"],
        rejected: ["pending"],
        awaiting_payment: ["paid", "cancelled"],
        paid: ["ready", "completed", "refused"],
        ready: ["completed", "refused"],
        completed: [],
        cancelled: [],
        refused: [],
    },
};
/** Разрешён ли переход from → to для заказа с данным способом оплаты. */
export function shopStatusTransitionAllowed(paymentMethod, from, to) {
    return (SHOP_ORDER_STATUS_FLOW[paymentMethod][from] ?? []).includes(to);
}
// ============================================================
// Методы доставки shop-заказа
// pickup — самовывоз со склада (QR на стойке)
// courier — «наш курьер»: мы везём по адресу (deliveryAddress/courierIntake/AI)
// self_courier — «свой курьер»: клиент сам заказал Яндекс.Доставку и прислал
//   ссылку на отслеживание (trackingUrl); оплата всегда дистанционная
// ============================================================
export const SHOP_DELIVERY_METHODS = ["pickup", "courier", "self_courier"];
export const SHOP_DELIVERY_METHOD_LABELS_RU = {
    pickup: "Самовывоз",
    courier: "Наш курьер",
    self_courier: "Свой курьер",
};
// ============================================================
// Zod-схемы для входных данных API
// ============================================================
export const shopCartItemSchema = z.object({
    productId: z.string().min(1),
    qty: z.number().int().positive().max(999),
});
export const createShopOrderSchema = z
    .object({
    items: z.array(shopCartItemSchema).min(1).max(50),
    method: z.enum(SHOP_DELIVERY_METHODS),
    paymentMethod: z.enum(SHOP_PAYMENT_METHODS).optional(),
    giftPump: z.boolean().optional(),
    deliveryAddress: z.string().min(1).max(500).optional(),
    totalWeightKg: z.number().min(0).optional(),
    vehicleHint: z.enum(["scooter_ok", "car_required", "truck_required"]).optional(),
    courierIntake: courierIntakeSchema.optional(),
    // «Свой курьер»: ссылка на отслеживание Яндекс.Доставки. Для self_courier
    // обязательна (см. superRefine ниже), для остальных методов игнорируется.
    trackingUrl: z
        .string()
        .url()
        .max(1000)
        // только http(s): javascript:/data:/file: проходят .url() и рендерятся
        // кликабельной ссылкой в админке (ревью 4.11)
        .refine((u) => /^https?:\/\//i.test(u), "Только http(s)-ссылка")
        .optional(),
})
    .superRefine((val, ctx) => {
    if (val.method === "self_courier" && !val.trackingUrl) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["trackingUrl"],
            message: "Для метода «Свой курьер» нужна ссылка на отслеживание",
        });
    }
});
//# sourceMappingURL=index.js.map